import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Typography,
  LinearProgress,
  Box,
  IconButton,
  Pagination,
  Chip,
  Avatar,
  Stack,
  Tooltip,
  TableSortLabel,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Grid,
  Divider
} from "@mui/material";
import {
  Visibility,
  Search,
  Warning,
  CheckCircle,
  Error,
  TrendingUp,
  TrendingDown,
  Inventory
} from "@mui/icons-material";
import { Link } from "react-router-dom";

const CategoryTable = ({ item, showUsage }) => {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const itemsPerPage = 10;

  // Filter items based on search term
  const filteredItems = item.items.filter(itemDetail =>
    itemDetail.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    itemDetail.stockType.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (itemDetail.proDependency && itemDetail.proDependency.some(dep => 
      dep.toLowerCase().includes(searchTerm.toLowerCase())
    ))
  );

  // Sort items
  const sortedItems = React.useMemo(() => {
    let sortableItems = [...filteredItems];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredItems, sortConfig]);

  // Calculate total pages
  const totalPages = Math.ceil(sortedItems.length / itemsPerPage);

  // Function to handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Function to handle sort
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Function to handle search
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1); // Reset to first page when searching
  };

  const calculateUsagePercentage = (totalStock = 0, remainingInStock = 0) => {
    if (
      typeof totalStock !== "number" ||
      typeof remainingInStock !== "number" ||
      totalStock === 0
    )
      return 0;
    const usedStock = totalStock - remainingInStock;
    return (usedStock / totalStock) * 100;
  };

  const calculateRemainingPercentage = (
    totalStock = 0,
    remainingInStock = 0
  ) => {
    if (
      typeof totalStock !== "number" ||
      typeof remainingInStock !== "number" ||
      totalStock === 0
    )
      return 0;
    return (remainingInStock / totalStock) * 100;
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return "success";
    if (percentage >= 60) return "info";
    if (percentage >= 40) return "warning";
    if (percentage >= 15) return "warning";
    return "info"; // Changed from error to info for a softer look
  };

  const getStockStatusIcon = (remainingStock, alertLevel) => {
    if (remainingStock === 0) return <TrendingDown sx={{ color: '#E91E63' }} />;
    if (remainingStock <= alertLevel) return <TrendingUp sx={{ color: '#FF9800' }} />;
    return <CheckCircle color="success" />;
  };

  const getStockStatusColor = (remainingStock, alertLevel) => {
    if (remainingStock === 0) return "secondary";
    if (remainingStock <= alertLevel) return "warning";
    return "success";
  };

  // Slice the items for the current page
  const displayedItems = sortedItems.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const hasValidCategory = item.category && item.category.trim() !== "";
  const hasValidDependencies = displayedItems.some(
    (itemDetail) => itemDetail?.proDependency?.length > 0
  );

  if (!hasValidCategory && !hasValidDependencies) {
    return null;
  }

  // Calculate summary statistics
  const totalItems = item.items.length;
  const lowStockItems = item.items.filter(item => item.remainingInStock <= item.stockAlert).length;
  const outOfStockItems = item.items.filter(item => item.remainingInStock === 0).length;
  const totalStockValue = item.items.reduce((sum, item) => sum + item.remainingInStock, 0);

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <Inventory />
                </Avatar>
                <Box>
                  <Typography variant="h6" component="div">
                    {totalItems}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Items
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <CheckCircle />
                </Avatar>
                <Box>
                  <Typography variant="h6" component="div">
                    {totalItems - lowStockItems}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Healthy Stock
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ bgcolor: '#FFF8E1', borderColor: '#FFB74D' }}>
            <CardContent sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: '#FF9800' }}>
                  <TrendingUp />
                </Avatar>
                <Box>
                  <Typography variant="h6" component="div">
                    {lowStockItems}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Need Attention
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ bgcolor: '#FCE4EC', borderColor: '#F8BBD9' }}>
            <CardContent sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ bgcolor: '#E91E63' }}>
                  <TrendingDown />
                </Avatar>
                <Box>
                  <Typography variant="h6" component="div">
                    {outOfStockItems}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Reorder Soon
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Search items by name, stock type, or dependencies..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            variant="outlined"
            size="small"
          />
        </CardContent>
      </Card>

      {/* Main Table */}
      <Paper elevation={2} sx={{ overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortConfig.key === 'name'}
                    direction={sortConfig.key === 'name' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    <strong>Item Name</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={sortConfig.key === 'remainingInStock'}
                    direction={sortConfig.key === 'remainingInStock' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('remainingInStock')}
                  >
                    <strong>Stock Level</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <strong>Stock Type</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Status</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Dependencies</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>{showUsage ? 'Usage' : 'Remaining'}</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedItems.map((itemDetail, index) => {
                const percentage = showUsage
                  ? calculateUsagePercentage(
                      itemDetail.totalStock,
                      itemDetail.remainingInStock
                    )
                  : calculateRemainingPercentage(
                      itemDetail.totalStock,
                      itemDetail.remainingInStock
                    );

                const stockStatus = getStockStatusColor(
                  itemDetail.remainingInStock,
                  itemDetail.stockAlert
                );

                return (
                  <TableRow 
                    key={itemDetail.id || index}
                    hover
                    sx={{ 
                      '&:hover': { 
                        bgcolor: 'action.hover' 
                      },
                      ...(itemDetail.remainingInStock === 0 && {
                        bgcolor: '#FCE4EC',
                        '&:hover': { bgcolor: '#F8BBD9' }
                      })
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getStockStatusIcon(itemDetail.remainingInStock, itemDetail.stockAlert)}
                        <Typography variant="body2" fontWeight="medium">
                          {itemDetail.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        {itemDetail.remainingInStock}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        of {itemDetail.totalStock}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={itemDetail.stockType} 
                        size="small" 
                        variant="outlined"
                        color="primary"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={
                          itemDetail.remainingInStock === 0
                            ? "📦 Reorder Soon"
                            : itemDetail.remainingInStock <= itemDetail.stockAlert
                            ? `📈 Consider Restocking`
                            : "✅ Good Stock"
                        }
                        sx={{
                          ...(itemDetail.remainingInStock === 0 && {
                            bgcolor: '#FCE4EC',
                            color: '#C2185B',
                            border: '1px solid #F8BBD9'
                          }),
                          ...(itemDetail.remainingInStock <= itemDetail.stockAlert && itemDetail.remainingInStock > 0 && {
                            bgcolor: '#FFF8E1',
                            color: '#E65100',
                            border: '1px solid #FFB74D'
                          }),
                          ...(itemDetail.remainingInStock > itemDetail.stockAlert && {
                            bgcolor: '#E8F5E8',
                            color: '#2E7D32',
                            border: '1px solid #C8E6C9'
                          })
                        }}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {itemDetail.proDependency && itemDetail.proDependency.length > 0 ? (
                        <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap">
                          {itemDetail.proDependency.slice(0, 2).map((dep, idx) => (
                            <Chip
                              key={idx}
                              label={dep}
                              size="small"
                              variant="outlined"
                              color="secondary"
                            />
                          ))}
                          {itemDetail.proDependency.length > 2 && (
                            <Tooltip title={itemDetail.proDependency.slice(2).join(', ')}>
                              <Chip
                                label={`+${itemDetail.proDependency.length - 2}`}
                                size="small"
                                variant="outlined"
                                color="default"
                              />
                            </Tooltip>
                          )}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          None
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ minWidth: 120 }}>
                        <LinearProgress
                          variant="determinate"
                          value={percentage}
                          color={getProgressColor(percentage)}
                          sx={{
                            height: 8,
                            borderRadius: 1,
                            mb: 1,
                          }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {Math.round(percentage)}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton
                          component={Link}
                          to={`/inventory/view-item/${itemDetail.id}`}
                          color="primary"
                          size="small"
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              variant="outlined"
              shape="rounded"
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}

        {/* No results message */}
        {filteredItems.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No items found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search criteria
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

CategoryTable.propTypes = {
  item: PropTypes.shape({
    category: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        remainingInStock: PropTypes.number.isRequired,
        totalStock: PropTypes.number.isRequired,
        category: PropTypes.string.isRequired,
        proDependency: PropTypes.arrayOf(PropTypes.string),
        stockAlert: PropTypes.number.isRequired,
        stockType: PropTypes.string.isRequired,
        id: PropTypes.string.isRequired,
      })
    ).isRequired,
  }).isRequired,
  showUsage: PropTypes.bool,
};

export default CategoryTable;